import { createSelector } from '@ngrx/store';
import { createSlice, noopReducer, PayloadAction } from 'ngrx-slice';

import { PageRequest } from '@shared/interfaces/page-request.interface';

import { StoredFilterPageResponse } from './../../../../../api/models/stored-filter-page-response';

export type FilterLocationType = 'PRODUCT_SORTIMENT_FILTER' | 'PRICING_FILTER';

export interface StoredFilterRequest extends PageRequest {
  type: FilterLocationType;
  pinned?: boolean;
}

export interface CreateStoredFilterRequest {
  name: string;
  location: FilterLocationType;
  filter: any;
}

export interface State {
  storedFilterRequest: StoredFilterRequest;
  storedFilterLoadError: boolean;
  storedFilterLoading: boolean;
  storedFilter: Array<StoredFilterPageResponse>;
  selectedStoredFilterId?: string;
}

export const initialState: State = {
  storedFilterRequest: {
    type: 'PRICING_FILTER',
    page: 0,
    size: 100,
  },
  storedFilterLoadError: false,
  storedFilterLoading: false,
  storedFilter: [],
  selectedStoredFilterId: undefined,
};

export const {
  actions: FilterStoreActions,
  selectors,
  ...FilterStoreFeature
} = createSlice({
  name: 'filterStore',
  initialState,
  reducers: {
    loadStoredFilters: {
      trigger: (
        state,
        action: PayloadAction<{ request: StoredFilterRequest }>
      ) => {
        state.storedFilter = [];
        state.storedFilterRequest = action.request;
        state.storedFilterLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{ response: Array<StoredFilterPageResponse> }>
      ) => {
        state.storedFilter = action.response;
        state.storedFilterLoading = false;
        state.storedFilterLoadError = false;
      },
      failure: (state) => ({
        ...state,
        storedFilterLoading: false,
        storedFilterLoadError: true,
      }),
    },
    createStoredFilter: {
      trigger: (
        _state,
        action: PayloadAction<{ request: CreateStoredFilterRequest }>
      ) => {},
      success: noopReducer(),
    },
    pinStoredFilter: {
      trigger: (
        _state,
        action: PayloadAction<{ request: { filterId: string } }>
      ) => {},
      success: (
        state,
        action: PayloadAction<{ response: { filterId: string } }>
      ) => {
        state.storedFilter = state.storedFilter.map((filter) =>
          filter.id === action.response.filterId
            ? { ...filter, pinned: !filter.pinned }
            : filter
        );
      },
    },
    applyStoredFilter: {
      trigger: (
        _state,
        action: PayloadAction<{
          request: {
            filterId: string;
            storedFilterUpdater: any;
            searchAction: any;
          };
        }>
      ) => {},
      success: (
        state,
        action: PayloadAction<{ response: { id?: string } }>
      ) => {
        state.selectedStoredFilterId = action.response.id;
      },
    },
    clearFilters: {
      trigger: (state) => {
        state.selectedStoredFilterId = undefined;
      },
      success: noopReducer(),
    },
    deleteStoredFilter: {
      trigger: (
        _state,
        action: PayloadAction<{
          request: { filterId: string; storedFilterUpdater: any };
        }>
      ) => {},
      success: (
        state,
        action: PayloadAction<{ response: { filterId: string } }>
      ) => {
        state.storedFilter = state.storedFilter.filter(
          (filter) => filter.id !== action.response.filterId
        );
      },
    },
    reset: (_) => initialState,
  },
});

export const FilterStoreSelectors = {
  ...selectors,
  selectPinnedFiltersList: createSelector(
    selectors.selectStoredFilter,
    (filters) => filters.filter((filter) => filter.pinned === true)
  ),
};
